import { ref } from 'vue'
import { hasProfile } from '@/utils/learnerProfile'
import router, { isHomePath, localizedRouteName } from '@/router'
import { ensureBackendUser } from '@/composables/auth'
import { demoLessonProps, shouldStartWithDemoLesson } from '@/composables/demoLesson'

// 模块级别的共享状态（单例）
const currentView = ref('HomePage')
const viewProps = ref({})
const previousView = ref(null)

export function useViews() {
  const handleViewChange = (newView, params = {}) => {
    if (newView === 'LevelTest' && !['listening', 'speaking'].includes(params.skill)) {
      newView = 'Profile'
    }

    // 如果是从 demo 返回到 RecentFiles，则改为返回到 HomePage
    if (currentView.value === 'AudioPlayer' && viewProps.value.isDemo && newView === 'RecentFiles') {
      newView = 'HomePage'
    }

    previousView.value = currentView.value
    currentView.value = newView

    // 合并现有的 props 和新的 params
    if (newView === 'AudioPlayer') {
      viewProps.value = {
        isDemo: false,
        ...params
      }
    } else {
      viewProps.value = {
        ...viewProps.value,
        ...params
      }
    }

    // 根据视图名称进行路由跳转
    switch (newView) {
      case 'CheckIn':
        router.push({ name: 'CheckIn' })
        break
      case 'Profile':
        router.push({ name: localizedRouteName('Profile', router.currentRoute.value.meta.locale) })
        break
      case 'LevelTest':
        router.push({
          name: localizedRouteName('LevelTest', router.currentRoute.value.meta.locale),
          query: params.skill ? { skill: params.skill, ...(params.setup ? { setup: '1' } : {}) } : {}
        })
        break
      case 'Material': {
        // 留在当前地址的语言版本里（/material 或 /zh/material）
        const pageLocale = router.currentRoute.value.meta.locale
        if (params.categoryId && params.collectionId) {
          router.push({
            name: localizedRouteName('MaterialCollection', pageLocale),
            params: { id: params.categoryId, collectionId: params.collectionId },
            // play：进入合集后直接打开这一条素材
            query: params.play ? { play: params.play } : {}
          })
        } else if (params.categoryId) {
          router.push({ name: localizedRouteName('MaterialCategory', pageLocale), params: { id: params.categoryId } })
        } else {
          router.push({ name: localizedRouteName('Material', pageLocale) })
        }
        break
      }
      case 'RecentFiles':
      case 'LearningRecords':
      case 'VocabularyBook':
      case 'Referral':
      case 'HomePage':
        if (!isHomePath(router.currentRoute.value.path)) {
          router.push('/')
        }
        break
    }
  }

  // 首页登录后，没有档案时先设置档案；已有档案再按原有规则进入示范或素材库。
  const enterWorkspaceAfterLogin = async (demoName) => {
    if (currentView.value !== 'HomePage') return
    const userData = await ensureBackendUser()
    if (userData && !hasProfile(userData.learnerProfile)) {
      handleViewChange('Profile')
    } else if (userData && shouldStartWithDemoLesson(userData)) {
      handleViewChange('AudioPlayer', demoLessonProps({ audioName: demoName, returnView: 'Material' }))
    } else {
      handleViewChange('Material')
    }
  }

  // 首次引导完成后：还没用过任何功能的新账号先进示范课学会跟读，其他账号去素材库
  const continueAfterOnboarding = async (demoName) => {
    const userData = await ensureBackendUser()
    if (userData && shouldStartWithDemoLesson(userData)) {
      handleViewChange('AudioPlayer', demoLessonProps({ audioName: demoName, returnView: 'Material' }))
    } else {
      handleViewChange('Material')
    }
  }

  return {
    currentView,
    contentProps: viewProps,
    previousView,
    handleViewChange,
    enterWorkspaceAfterLogin,
    continueAfterOnboarding
  }
}
